import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Briefcase,
  FileText,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  Filter,
  ArrowLeft,
  Settings,
  MessageSquare,
  User,
  Star,
  Calendar,
  Loader2,
  Send,
  Eye,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ProfessionalDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("quotations");
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    enabled: false,
    accepted_categories: [],
    accepted_services: [],
    priorities: {
      "1_hour": true,
      "3_hours": true,
      "same_day": true,
      "24_hours": true,
      "48_hours": true,
      "72_hours": true,
    },
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseForm, setResponseForm] = useState({
    amount: "",
    description: "",
    valid_until: "",
    terms: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuotations();
    fetchSettings();
  }, [statusFilter, priorityFilter]);

  const fetchQuotations = async () => {
    try {
      let url = `${API}/quotations/received?limit=50`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (priorityFilter !== "all") url += `&priority=${priorityFilter}`;

      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setQuotations(data);
      }
    } catch (error) {
      console.error("Error fetching quotations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API}/professional/quotation-settings`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await fetch(`${API}/professional/quotation-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newSettings),
      });

      if (response.ok) {
        setSettings(newSettings);
        toast.success("Configuración actualizada");
      }
    } catch (error) {
      toast.error("Error al actualizar configuración");
    }
  };

  const handleRespond = async () => {
    if (!selectedQuotation) return;

    setSubmitting(true);
    try {
      const response = await fetch(
        `${API}/quotations/${selectedQuotation.quotation_id}/respond`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            quotation_id: selectedQuotation.quotation_id,
            amount: parseFloat(responseForm.amount),
            description: responseForm.description,
            valid_until: responseForm.valid_until,
            terms: responseForm.terms,
          }),
        }
      );

      if (response.ok) {
        toast.success("Cotización enviada");
        setShowResponseModal(false);
        setSelectedQuotation(null);
        setResponseForm({ amount: "", description: "", valid_until: "", terms: "" });
        fetchQuotations();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al enviar cotización");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      "1_hour": { label: "1 Hora", color: "bg-red-600" },
      "3_hours": { label: "3 Horas", color: "bg-orange-600" },
      "same_day": { label: "Mismo día", color: "bg-yellow-600" },
      "24_hours": { label: "24 Horas", color: "bg-blue-600" },
      "48_hours": { label: "48 Horas", color: "bg-indigo-600" },
      "72_hours": { label: "72 Horas", color: "bg-purple-600" },
    };
    const c = config[priority] || { label: priority, color: "bg-slate-600" };
    return <Badge className={`${c.color} text-white`}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: "Pendiente", color: "bg-yellow-600", icon: Clock },
      quoted: { label: "Cotizado", color: "bg-blue-600", icon: FileText },
      accepted: { label: "Aceptado", color: "bg-green-600", icon: CheckCircle },
      rejected: { label: "Rechazado", color: "bg-red-600", icon: XCircle },
      expired: { label: "Expirado", color: "bg-slate-600", icon: AlertCircle },
    };
    const c = config[status] || { label: status, color: "bg-slate-600", icon: AlertCircle };
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  const formatDeadline = (deadline) => {
    const d = new Date(deadline);
    const now = new Date();
    const diff = d - now;

    if (diff < 0) return "Expirado";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} horas`;
    return d.toLocaleDateString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="min-h-screen bg-[#0e1621]">
      {/* Header */}
      <div className="bg-[#17212b] border-b border-[#232e3c] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/chat")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-red-500" />
                Dashboard Profesional
              </h1>
              <p className="text-sm text-slate-400">Gestiona tus cotizaciones y servicios</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-red-600 text-white">
                {user?.name?.charAt(0) || "P"}
              </AvatarFallback>
            </Avatar>
            <div className="text-right">
              <p className="text-white font-medium">{user?.name}</p>
              <p className="text-xs text-slate-400">Profesional</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#17212b] border border-[#232e3c]">
            <TabsTrigger value="quotations" className="data-[state=active]:bg-red-600">
              <FileText className="w-4 h-4 mr-2" />
              Cotizaciones
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-red-600">
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-red-600">
              <Star className="w-4 h-4 mr-2" />
              Estadísticas
            </TabsTrigger>
          </TabsList>

          {/* Cotizaciones Tab */}
          <TabsContent value="quotations" className="mt-6">
            {/* Filters */}
            <Card className="bg-[#17212b] border-[#232e3c] mb-6">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400 text-sm">Filtros:</span>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40 bg-[#0e1621] border-[#232e3c] text-white">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#17212b] border-[#232e3c]">
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pending">Pendientes</SelectItem>
                      <SelectItem value="quoted">Cotizados</SelectItem>
                      <SelectItem value="accepted">Aceptados</SelectItem>
                      <SelectItem value="rejected">Rechazados</SelectItem>
                      <SelectItem value="expired">Expirados</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger className="w-40 bg-[#0e1621] border-[#232e3c] text-white">
                      <SelectValue placeholder="Prioridad" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#17212b] border-[#232e3c]">
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="1_hour">1 Hora</SelectItem>
                      <SelectItem value="3_hours">3 Horas</SelectItem>
                      <SelectItem value="same_day">Mismo día</SelectItem>
                      <SelectItem value="24_hours">24 Horas</SelectItem>
                      <SelectItem value="48_hours">48 Horas</SelectItem>
                      <SelectItem value="72_hours">72 Horas</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex-1" />
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-sm">Cotizaciones activas:</span>
                    <Switch
                      checked={settings.enabled}
                      onCheckedChange={(checked) => updateSettings({ ...settings, enabled: checked })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quotations List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-red-600" />
              </div>
            ) : quotations.length === 0 ? (
              <Card className="bg-[#17212b] border-[#232e3c]">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-xl text-white mb-2">No hay cotizaciones</h3>
                  <p className="text-slate-400">
                    {settings.enabled
                      ? "Aún no has recibido solicitudes de cotización"
                      : "Activa las cotizaciones para recibir solicitudes"}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {quotations.map((q) => (
                  <Card
                    key={q.quotation_id}
                    className="bg-[#17212b] border-[#232e3c] hover:border-red-600/50 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedQuotation(q);
                      if (q.status === "pending") setShowResponseModal(true);
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <Avatar className="w-12 h-12">
                            <AvatarFallback className="bg-slate-700 text-white">
                              {q.requester_name?.charAt(0) || "U"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-white font-medium">{q.requester_name}</h4>
                              {getStatusBadge(q.status)}
                              {getPriorityBadge(q.priority)}
                            </div>
                            <p className="text-slate-400 text-sm mb-2">{q.service}</p>
                            <p className="text-slate-300 text-sm line-clamp-2">{q.description}</p>
                            {q.budget_min && q.budget_max && (
                              <p className="text-emerald-400 text-sm mt-2 flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                Presupuesto: ${q.budget_min} - ${q.budget_max}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-400 flex items-center gap-1 justify-end">
                            <Clock className="w-4 h-4" />
                            {formatDeadline(q.deadline)}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(q.created_at).toLocaleDateString("es-MX")}
                          </p>
                          {q.status === "pending" && (
                            <Button
                              size="sm"
                              className="mt-2 bg-red-600 hover:bg-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedQuotation(q);
                                setShowResponseModal(true);
                              }}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Cotizar
                            </Button>
                          )}
                          {q.status === "quoted" && q.response && (
                            <p className="text-emerald-400 font-bold mt-2">
                              ${q.response.amount}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Enable/Disable */}
              <Card className="bg-[#17212b] border-[#232e3c]">
                <CardHeader>
                  <CardTitle className="text-white">Estado del módulo</CardTitle>
                  <CardDescription className="text-slate-400">
                    Activa o desactiva la recepción de cotizaciones
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 bg-[#0e1621] rounded-lg">
                    <div>
                      <p className="text-white font-medium">Recibir cotizaciones</p>
                      <p className="text-sm text-slate-400">
                        {settings.enabled ? "Estás aceptando solicitudes" : "No estás recibiendo solicitudes"}
                      </p>
                    </div>
                    <Switch
                      checked={settings.enabled}
                      onCheckedChange={(checked) => updateSettings({ ...settings, enabled: checked })}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Priority Settings */}
              <Card className="bg-[#17212b] border-[#232e3c]">
                <CardHeader>
                  <CardTitle className="text-white">Prioridades aceptadas</CardTitle>
                  <CardDescription className="text-slate-400">
                    Selecciona qué tiempos de respuesta aceptas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { key: "1_hour", label: "Máximo 1 hora", desc: "Respuesta urgente" },
                      { key: "3_hours", label: "Máximo 3 horas", desc: "Alta prioridad" },
                      { key: "same_day", label: "Mismo día (hasta 4pm)", desc: "Durante el día" },
                      { key: "24_hours", label: "24 horas", desc: "Un día hábil" },
                      { key: "48_hours", label: "48 horas", desc: "Dos días hábiles" },
                      { key: "72_hours", label: "72 horas", desc: "Tres días hábiles" },
                    ].map((priority) => (
                      <div
                        key={priority.key}
                        className="flex items-center justify-between p-3 bg-[#0e1621] rounded-lg"
                      >
                        <div>
                          <p className="text-white text-sm">{priority.label}</p>
                          <p className="text-xs text-slate-500">{priority.desc}</p>
                        </div>
                        <Switch
                          checked={settings.priorities?.[priority.key] ?? true}
                          onCheckedChange={(checked) =>
                            updateSettings({
                              ...settings,
                              priorities: { ...settings.priorities, [priority.key]: checked },
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Services */}
              <Card className="bg-[#17212b] border-[#232e3c] md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white">Servicios que ofreces</CardTitle>
                  <CardDescription className="text-slate-400">
                    Define los servicios para los que aceptas cotizaciones
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {settings.accepted_services?.map((service, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="bg-red-600/20 border-red-600 text-red-400 cursor-pointer hover:bg-red-600/30"
                        onClick={() =>
                          updateSettings({
                            ...settings,
                            accepted_services: settings.accepted_services.filter((_, i) => i !== idx),
                          })
                        }
                      >
                        {service} ×
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="new-service"
                      placeholder="Agregar servicio..."
                      className="bg-[#0e1621] border-[#232e3c] text-white"
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && e.target.value.trim()) {
                          updateSettings({
                            ...settings,
                            accepted_services: [...(settings.accepted_services || []), e.target.value.trim()],
                          });
                          e.target.value = "";
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      className="border-red-600 text-red-400"
                      onClick={() => {
                        const input = document.getElementById("new-service");
                        if (input.value.trim()) {
                          updateSettings({
                            ...settings,
                            accepted_services: [...(settings.accepted_services || []), input.value.trim()],
                          });
                          input.value = "";
                        }
                      }}
                    >
                      Agregar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats" className="mt-6">
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Total recibidas", value: quotations.length, icon: FileText, color: "text-blue-400" },
                { label: "Pendientes", value: quotations.filter((q) => q.status === "pending").length, icon: Clock, color: "text-yellow-400" },
                { label: "Aceptadas", value: quotations.filter((q) => q.status === "accepted").length, icon: CheckCircle, color: "text-green-400" },
                { label: "Tasa de aceptación", value: `${Math.round((quotations.filter((q) => q.status === "accepted").length / Math.max(quotations.filter((q) => q.status === "quoted").length, 1)) * 100)}%`, icon: Star, color: "text-red-400" },
              ].map((stat, idx) => (
                <Card key={idx} className="bg-[#17212b] border-[#232e3c]">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-400 text-sm">{stat.label}</p>
                        <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                      </div>
                      <stat.icon className={`w-8 h-8 ${stat.color} opacity-50`} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Response Modal */}
      <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
        <DialogContent className="bg-[#17212b] border-[#232e3c] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Responder cotización</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedQuotation?.requester_name} - {selectedQuotation?.service}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Monto de la cotización</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  value={responseForm.amount}
                  onChange={(e) => setResponseForm({ ...responseForm, amount: e.target.value })}
                  className="bg-[#0e1621] border-[#232e3c] text-white pl-9"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Descripción de la cotización</Label>
              <Textarea
                value={responseForm.description}
                onChange={(e) => setResponseForm({ ...responseForm, description: e.target.value })}
                className="bg-[#0e1621] border-[#232e3c] text-white mt-1"
                placeholder="Detalle qué incluye su cotización..."
                rows={3}
              />
            </div>
            <div>
              <Label className="text-slate-300">Válido hasta</Label>
              <Input
                type="datetime-local"
                value={responseForm.valid_until}
                onChange={(e) => setResponseForm({ ...responseForm, valid_until: e.target.value })}
                className="bg-[#0e1621] border-[#232e3c] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Términos y condiciones (opcional)</Label>
              <Textarea
                value={responseForm.terms}
                onChange={(e) => setResponseForm({ ...responseForm, terms: e.target.value })}
                className="bg-[#0e1621] border-[#232e3c] text-white mt-1"
                placeholder="Condiciones de pago, garantías, etc."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponseModal(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button
              onClick={handleRespond}
              disabled={submitting || !responseForm.amount || !responseForm.description}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar cotización
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfessionalDashboard;
